// Shared rules for test-out sections. Authoring checks mirror
// backend/lms_info/section_test_out.py, which is what actually enforces them.

const isRequired = (block, dataProp) => {
  const props = block?.props || {};
  const data = props[dataProp] || {};
  return Boolean(data.required ?? props.required ?? true);
};

// The first required record in a section's blocks, or null. These sections can't
// be skipped, because a quiz result must not stand in for one of these.
export function testOutBlockingRecord(blocks) {
  for (const block of blocks || []) {
    const type = block?.type;
    if (type === "signature") return "a signature";
    if (type === "checkpoint") return "a checkpoint";
    if (type === "documentUpload" && isRequired(block, "documentUploadBlockData")) {
      return "a document upload";
    }
    if (type === "managerInput" && isRequired(block, "managerInputBlockData")) {
      return "a manager input";
    }
    if (type === "form" && isRequired(block, "formSelected")) {
      return "a required form";
    }
  }
  return null;
}

export const isTestOutQuiz = (quiz) => quiz?.gate_on_pass === false;

// Dropdown options for picking a test-out quiz. The dropdown works on plain strings
// and a choice is found by its label, so every label has to be unique, including
// against a quiz actually named "Safety (2)".
export function testOutQuizOptions(quizzes) {
  const used = new Set();
  return (quizzes || []).filter(isTestOutQuiz).map((quiz) => {
    const name = quiz.name || "Untitled quiz";
    let label = name;
    for (let copy = 2; used.has(label); copy++) {
      label = `${name} (${copy})`;
    }
    used.add(label);
    return { label, uuid: quiz.uuid, name };
  });
}

// Uuids of the quizzes embedded in a section's blocks.
export function embeddedQuizUuids(blocks) {
  const uuids = new Set();
  for (const block of blocks || []) {
    if (block?.type !== "quiz") continue;
    const uuid = block.props?.quizSelected?.uuid || block.props?.quizUuid;
    if (uuid) uuids.add(String(uuid));
  }
  return uuids;
}

// Names of the sections that passing a quiz in these blocks would skip. A section
// shared between modules is only listed once.
export function sectionsSkippedByBlocks(blocks, sections) {
  const quizUuids = embeddedQuizUuids(blocks);
  const seen = new Set();
  const names = [];
  for (const section of sections || []) {
    const quizUuid = section?.test_out_quiz?.uuid;
    if (!quizUuid || !quizUuids.has(String(quizUuid)) || seen.has(section.uuid)) continue;
    seen.add(section.uuid);
    names.push(section.name);
  }
  return names;
}

// The position the learner moves to next. Tested-out sections are passed over
// unless reviewing, when every section is walked in order. Returns null at the end.
export function findNextSectionPosition(modules, moduleIndex, sectionIndex, skipTestedOut) {
  const allModules = modules || [];
  for (let m = moduleIndex; m < allModules.length; m++) {
    const sections = allModules[m]?.sections || [];
    for (let s = m === moduleIndex ? sectionIndex + 1 : 0; s < sections.length; s++) {
      if (skipTestedOut && sections[s]?.annotated_enrollment?.is_tested_out) continue;
      return { moduleIndex: m, sectionIndex: s };
    }
  }
  return null;
}
